'use client';

import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import {useEffect, useState} from 'react';

import {Loader} from '@web-landing/components/Loader/Loader';
import {OmicronContext} from '@web-landing/components/OmicronClientContext/OmicronClientContext';

import {requestWaitingTime} from '@web-landing/constants';

import {makeResponseSafe} from '@utils/omicron';
import {getDeviceInfo} from '@utils/omicron/getDeviceInfo';
import {setOidSegments} from '@utils/omicron/setOidSegments';
import {omicronFallback} from '@web-landing/omicron/fetchOmicron';
import {
  OmicronConfigSchema,
  OmicronResponse,
} from '@web-landing/omicron/schemas';

import {fetchWithLogs} from '@web-landing//utils/fetch';

type Props = {
  omicronUrl?: string;
  omicronUrlConfigId?: string;
  omicronUrlEnv?: string;
  children: React.ReactNode;
};

export const OmicronClientProvider = ({
  children,
  omicronUrl,
  omicronUrlConfigId,
  omicronUrlEnv,
}: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [omicronResponse, setOmicronResponse] = useState<OmicronResponse>();
  const {os, device} = getDeviceInfo();

  useEffect(() => {
    const configId = omicronUrlConfigId as string;
    const env = omicronUrlEnv as string;
    const params = new URLSearchParams({
      deviceType: device,
      os: os,
      config_id: configId,
      app_env: env,
      service: 'vk-store-web-landing',
    });

    const abortController = new AbortController();
    const signal = abortController.signal;

    const timeoutId = setTimeout(() => {
      abortController.abort();
      setIsLoading(false);
    }, requestWaitingTime);

    fetchWithLogs(`${omicronUrl}/api/v1/get?${params.toString()}`, 'json', {
      signal,
      credentials: 'include',
    })
      .then((res: OmicronResponse) => {
        setOidSegments(res);
        setOmicronResponse(makeResponseSafe(OmicronConfigSchema, res));
      })
      .catch(() => {
        setOmicronResponse(omicronFallback);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setIsLoading(false);
      });

    return () => {
      abortController.abort();
    };
  }, [omicronUrl, omicronUrlConfigId, omicronUrlEnv]);

  return (
    <OmicronContext.Provider value={omicronResponse as OmicronResponse}>
      {isLoading && <Loader />}
      {children}
    </OmicronContext.Provider>
  );
};
